import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useLinksQuery } from '@platform/state/api';
import { isFieldRestricted } from '../../fields/fieldRestricted';
import { FieldRenderer } from '../../fields/FieldRenderer';
import { miniFieldsOf } from '../formVariants';
import { recordCode } from '../header/recordCode';
import { useAutosave } from '../useAutosave';
import { useRecordEditor } from '../useRecordEditor';
import { valueOf } from '../valueOf';
import type { IMiniFormBodyProps } from './IMiniFormBodyProps';

/** Тип записів трасування міні-форми (прототип v4 qrLinks: «Трасовані завдання» — зв'язки із завданнями). */
const TRACED = 'task';

/**
 * Вміст міні-форми (UIUX forms/form-variants, прототип v4 qr): назва 40px, поля варіанта `mini` (типово статус · пріоритет · термін у
 * три колонки, виконавець), опис, якщо тип його має; вибір зберігається одразу, текст — після виходу з поля, незмінене значення — ні (крім
 * повторного призначення чинного виконавця Owner/Admin, useAutosave); знизу — «Трасовані завдання»
 * (зв'язки із завданнями: пігулка типу, код, назва — відкриває завдання) з власною прокруткою. Поле з персональними даними, приховане
 * сервером, — без значення й без редагування.
 */
export function MiniFormBody({ ws, rt, record, onClose }: IMiniFormBodyProps) {
  const { t } = useTranslation();
  const { editor, commit } = useAutosave(useRecordEditor(ws, rt.key, record, { editing: true }), rt, ws, record);
  const { data: links = [] } = useLinksQuery(record.id);
  const canEdit = record.permissions?.edit !== false;
  const configured = miniFieldsOf(rt);
  const description = rt.fields.find((f) => f.key === 'description');
  const fields = description && !configured.includes(description) ? [...configured, description] : configured;
  const traced = links.filter((l) => l.otherRecordType === TRACED);
  const width = (key: string) => (['status', 'priority', 'due'].includes(key) ? 'S' : key === 'assignee' ? 'M' : 'L');
  return (
    <div className="rec-mini" onBlur={commit}>
      <input className="control rec-mini__title" value={String(valueOf(record, editor.draft, 'title') ?? '')} readOnly={!canEdit}
        aria-label={t('recordPage.titleLabel')} placeholder={t('recordPage.mini.untitled')} onChange={(e) => editor.setField('title', e.target.value)} />
      <div className="rec-mini__grid">
        {fields.map((f) => (
          <FieldRenderer key={f.key} field={f} rt={rt} ws={ws} record={record} value={valueOf(record, editor.draft, f.key)} onChange={editor.setField} readOnly={!canEdit}
            width={width(f.key)} ownLine={false} error={editor.fieldErrors[f.key]} compact restricted={isFieldRestricted(record, f)} idPrefix="mf"
            placeholder={f === description ? t('fieldControls.addDescription') : undefined} />
        ))}
      </div>
      <div className="rec-mini__traced">
        <div className="rec-mini__thead"><span className="rec-section__title">{t('recordPage.mini.traced')}</span><span>{t('recordPage.mini.tracedCount', { count: traced.length })}</span></div>
        <div className="rec-mini__list">
          {traced.map((l) => (
            <Link key={l.id} className="rec-mini__link" to={routes.record(l.otherModule, l.otherRecordType, l.otherRecordId)} onClick={onClose}>
              <span className={`link-pill link-pill--${l.linkType}`}>{t(`recordPage.linkTypes.${l.linkType}`, { defaultValue: l.linkLabel })}</span>
              <span className="rtab-rel__code" title={l.otherDisplay}>{recordCode(l.otherDisplay, l.otherCode)}</span><span className="rec-mini__ltitle">{l.otherTitle}</span>
            </Link>
          ))}
          {!traced.length && <div className="rec-relcard__empty">{t('recordPage.mini.noTraced')}</div>}
        </div>
      </div>
    </div>
  );
}
